/**
 * LiveFeedTicker — single-line "trading floor" ticker that displays
 * SYSTEM-WIDE network activity (anonymized real claims of other users
 * across the platform) with a label "СЕТЬ" so the user understands
 * these amounts don't credit their own balance.
 *
 * Renders ONLY the latest event with a Motion enter/exit transition,
 * so the visible DOM is always 1 row — no virtualization needed.
 *
 * Right side: hybrid countdown to the next meaningful real event
 * (готовый claim / ближайший PC автосбор / новая дневная ставка).
 */
import { memo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { NetworkEvent } from '@/hooks/useDashboardFire';

export interface LiveEvent {
  id: number;
  short: string;
  amount: number;
  ts: number;
}

interface LiveFeedTickerProps {
  /** Сетевое событие — реальный клейм юзера платформы */
  networkEvent: NetworkEvent | null;
  /** Сколько юзеров онлайн (active < 600 сек) */
  onlineCount?: number;
}

// Always Europe/Moscow so all users see the same wall-clock time.
const MOSCOW_TIME_FMT = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

// MOSCOW_TIME_FMT оставляем на будущее — пока не используется
// (формат "N мин назад" живее абсолютного времени)
void MOSCOW_TIME_FMT;

/**
 * "N sec/min/h ago" — локализовано.
 * Обновляется вместе с nowMs (1Hz tick).
 */
import type { TFunction } from 'i18next';
function formatRelative(ts: number, nowMs: number, t: TFunction): string {
  const diffSec = Math.max(0, Math.floor((nowMs - ts) / 1000));
  if (diffSec < 10) return t('nodes.feed.justNow');
  if (diffSec < 60) return t('nodes.feed.secAgo', { s: diffSec });
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t('nodes.feed.minAgo', { m: diffMin });
  const diffH = Math.floor(diffMin / 60);
  return t('nodes.feed.hourAgo', { h: diffH });
}

// formatHms и secondsUntilMoscowMidnight удалены — таймеры ушли
// в ClaimStatusPill. Здесь остаётся только сетевой событийный стрим.

function LiveFeedTickerImpl({
  networkEvent,
  onlineCount = 0,
}: LiveFeedTickerProps) {
  const { t } = useTranslation();
  // Локальный 1Hz тик для пересчёта "N мин назад".
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="lg-pill !rounded-2xl flex items-center gap-2 px-3 py-2 relative overflow-hidden h-[34px]">
      {/* Pulsing live indicator */}
      <span className="relative flex-shrink-0 inline-flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full rounded-full opacity-70 bg-[var(--status-positive)] animate-ping" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--status-positive)]" />
      </span>
      <Activity
        size={11}
        strokeWidth={2}
        className="text-[var(--text-muted)] flex-shrink-0"
      />

      {/* Сетевое событие — общая активность платформы */}
      <div className="relative flex-1 h-full overflow-hidden">
        <AnimatePresence mode="popLayout" initial={false}>
          {networkEvent ? (
            <motion.div
              key={networkEvent.ts}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
              className="absolute inset-0 flex items-center gap-2 max-[480px]:gap-1"
            >
              {/* Префикс СЕТЬ. На iOS — видимо (на айфоне влезает при
                  font -2px). На Android (html.platform-non-ios) — прячем
                  на узких экранах, т.к. там всё равно не помещается
                  даже при уменьшенном шрифте (Poco X7 Pro и т.п.). */}
              <span className="text-[8px] font-mono font-bold uppercase tracking-[0.12em] text-[var(--accent-secondary)] flex-shrink-0 max-[480px]:[.platform-non-ios_&]:hidden">
                {t('nodes.feed.network')}
              </span>
              {/* Ник юзера. Шрифт -2px везде; на Android narrow ещё уже */}
              <span className="text-[8px] font-mono font-semibold text-[var(--text-secondary)] flex-shrink-0 max-w-[90px] truncate max-[480px]:[.platform-non-ios_&]:max-w-[72px]">
                {networkEvent.anonId}
              </span>
              <span className="text-[9px] font-mono font-bold text-[var(--status-positive)] flex-shrink-0">
                +{networkEvent.amount.toFixed(2)}
              </span>
              <span className="text-[8px] font-mono uppercase tracking-[0.08em] text-[var(--text-muted)] flex-shrink-0 leading-none">
                USD₮
              </span>
              <span className="text-[9px] font-mono text-[var(--text-muted)] ml-auto flex-shrink-0 max-[480px]:[.platform-non-ios_&]:hidden">
                {formatRelative(networkEvent.ts, nowMs, t)}
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="placeholder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 flex items-center"
            >
              <span className="text-[10px] font-mono text-[var(--text-disabled)] tracking-[0.06em]">
                {t('nodes.feed.waiting')}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Разделитель + онлайн-счётчик (active < 600 сек).
          Компактная версия на всех устройствах: только иконка+цифра,
          слово "online" скрыто чтоб освободить место тикеру. */}
      <div className="flex items-center gap-1.5 flex-shrink-0 border-l border-[var(--glass-stroke)] pl-3 pr-[2px]">
        <Users
          size={11}
          strokeWidth={2.2}
          className="text-[var(--status-positive)] flex-shrink-0"
        />
        <span className="text-[11px] font-mono font-bold text-[var(--text-secondary)] tabular-nums">
          {onlineCount}
        </span>
      </div>
    </div>
  );
}

/**
 * Memoized export so the ticker only re-renders when its `event` prop
 * actually changes (which happens at ~0.5Hz, not at the 1Hz countdown
 * frequency that lives inside it).
 */
export const LiveFeedTicker = memo(LiveFeedTickerImpl);
